import { arrayMove } from '@dnd-kit/sortable'
import { getEncryptionIds } from '@shared/ast/astUtils'
import { extractSectionFromChapterSectionHeading } from '@shared/ast/extractChapterSection'
import { collectChapterSectionHeadingsWithAncestorsFromAuthorAst } from '@shared/ast/tocChapterSectionCollect'
import {
  type ChapterSectionDependency,
  type DependencyKind,
  findDependency,
  generateDependencyId,
} from '@shared/chapterSectionDependencies'
import {
  type Chapter,
  canonicalChapterSectionKeysFromServer,
  chapterSectionKey,
  generateChapterId,
  mergeChaptersWithCanonicalKeys,
} from '@shared/chapterSectionsOrder'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { Book, ContentFile, apiService } from '../../../services/api'
import { ContentAST } from '../../../transforms/ast'
import { logger } from '../../../utils/logger'

type ChapterSectionRowFromServer = {
  filename: string
  headingBlockId: string
  headingText: string
  displayText: string
  ancestorLabels?: string[]
  tagIds?: string[]
  needsRepublish?: boolean
}

export type ChapterSectionEntry = {
  chapter: ContentFile
  headingBlockId: string
  text: string
  displayText: string
  ancestorLabels: string[]
  tagIds: string[]
  /** Published, but an encrypted passage inside it has no glyphs — readers see a hole. */
  needsRepublish: boolean
}

export type ChapterGroup = {
  chapter: Chapter
  entries: ChapterSectionEntry[]
}

type UseChapterSectionsBookArgs = {
  bookId: string | undefined
  isAuthor: boolean
  chapters: ContentFile[]
  currentChapter: ContentFile | null
  currentContent: ContentAST | null
  book: Book | null
  setBook: React.Dispatch<React.SetStateAction<Book | null>>
  bookRef: React.MutableRefObject<Book | null>
  chaptersRef: React.MutableRefObject<ContentFile[]>
}

/**
 * True when a published section shows readers a hole: it holds an encrypted passage
 * that has no glyphs yet. Mirrors `sectionNeedsRepublish` on the server, which scores
 * every other file; this one scores the file open in the editor.
 */
function sectionNeedsRepublish(
  ast: ContentAST,
  headingBlockId: string,
  publishedSections: Set<string>,
  glyphSectionIds: Set<string>,
): boolean {
  if (!publishedSections.has(headingBlockId)) {
    return false
  }
  const extracted = extractSectionFromChapterSectionHeading(ast, headingBlockId)
  if (!extracted) {
    return false
  }
  return getEncryptionIds(extracted.slice).some((id) => !glyphSectionIds.has(id))
}

function getBookChapters(book: Book | null): Chapter[] | undefined {
  return book?.settings?.chapters as Chapter[] | undefined
}

/**
 * Chapter sections (^) TOC: server scan + merge with book.settings.chapters, drag reorder, initial seed.
 */
export function useChapterSectionsBook({
  bookId,
  isAuthor,
  chapters,
  currentChapter,
  currentContent,
  book,
  setBook,
  bookRef,
  chaptersRef,
}: UseChapterSectionsBookArgs) {
  const currentChapterDefs = getBookChapters(book)
  const [chapterSectionsFromServer, setChapterSectionsFromServer] = useState<ChapterSectionRowFromServer[] | null>(null)
  const chaptersInitRef = useRef(false)

  useEffect(() => {
    if (!bookId || !isAuthor) {
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await apiService.getChapterSectionsToc(bookId)
        if (!cancelled && res.success && Array.isArray(res.data)) {
          setChapterSectionsFromServer(res.data)
        }
      } catch {
        // Leave chapterSectionsFromServer at its previous value — a transient
        // failure shouldn't empty the sidebar. A later save/refetch will fill it.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bookId, isAuthor])

  const canonicalChapterSectionEntries = useMemo(() => {
    const ordered = [...chapters].sort((a, b) => a.order - b.order)
    const byFile = new Map<
      string,
      Array<{
        headingBlockId: string
        text: string
        displayText: string
        ancestorLabels: string[]
        tagIds: string[]
        needsRepublish: boolean
      }>
    >()

    if (chapterSectionsFromServer) {
      for (const row of chapterSectionsFromServer) {
        const list = byFile.get(row.filename) ?? []
        list.push({
          headingBlockId: row.headingBlockId,
          text: row.headingText,
          displayText: row.displayText,
          ancestorLabels: row.ancestorLabels ?? [],
          tagIds: row.tagIds ?? [],
          needsRepublish: Boolean(row.needsRepublish),
        })
        byFile.set(row.filename, list)
      }
    }

    if (chapterSectionsFromServer !== null && currentChapter && currentContent) {
      const tagIdsByBlockId = new Map<string, string[]>()
      for (const row of chapterSectionsFromServer) {
        if (row.filename !== currentChapter.filename) {
          continue
        }
        tagIdsByBlockId.set(row.headingBlockId, row.tagIds ?? [])
      }
      // The open file is scored against the AST in the editor, not the server's copy:
      // marking a passage for encryption must flag the section before the next TOC fetch.
      const publishedHere = new Set(currentChapter.publishedSections ?? [])
      const glyphIds = new Set((currentChapter.encryptedSections ?? []).map((es) => es.sectionId))
      const live = collectChapterSectionHeadingsWithAncestorsFromAuthorAst(currentContent).map((section) => ({
        headingBlockId: section.blockId,
        text: section.text,
        displayText: section.displayText,
        ancestorLabels: section.ancestorLabels,
        tagIds: tagIdsByBlockId.get(section.blockId) ?? [],
        needsRepublish: sectionNeedsRepublish(currentContent, section.blockId, publishedHere, glyphIds),
      }))
      byFile.set(currentChapter.filename, live)
    }

    const out: ChapterSectionEntry[] = []
    const seenChapterSectionKeys = new Set<string>()
    for (const ch of ordered) {
      const rows = byFile.get(ch.filename)
      if (!rows?.length) {
        continue
      }
      for (const row of rows) {
        const dedupeKey = `${ch.filename}:${row.headingBlockId}`
        if (seenChapterSectionKeys.has(dedupeKey)) {
          continue
        }
        seenChapterSectionKeys.add(dedupeKey)
        out.push({
          chapter: ch,
          headingBlockId: row.headingBlockId,
          text: row.text,
          displayText: row.displayText,
          ancestorLabels: row.ancestorLabels,
          tagIds: row.tagIds,
          needsRepublish: row.needsRepublish,
        })
      }
    }
    return out
  }, [chapters, chapterSectionsFromServer, currentChapter, currentContent])

  const entryByKey = useMemo(
    () =>
      new Map(
        canonicalChapterSectionEntries.map((row) => [chapterSectionKey(row.chapter.filename, row.headingBlockId), row]),
      ),
    [canonicalChapterSectionEntries],
  )

  const chapterGroups: ChapterGroup[] = useMemo(() => {
    const canonicalKeys = canonicalChapterSectionEntries.map((row) =>
      chapterSectionKey(row.chapter.filename, row.headingBlockId),
    )

    if (!currentChapterDefs?.length) {
      const entries = canonicalKeys.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null)
      if (entries.length === 0) {
        return []
      }
      return [{ chapter: { id: '__default__', title: '', chapterSections: canonicalKeys }, entries }]
    }

    const { chapters: merged, unattached } = mergeChaptersWithCanonicalKeys(currentChapterDefs, canonicalKeys)
    const groups: ChapterGroup[] = merged.map((chDef) => ({
      chapter: chDef,
      entries: chDef.chapterSections.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null),
    }))
    if (unattached.length > 0) {
      groups.push({
        chapter: { id: '__unattached__', title: '', chapterSections: unattached },
        entries: unattached.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null),
      })
    }
    return groups
  }, [currentChapterDefs, canonicalChapterSectionEntries, entryByKey])

  const chapterSectionEntries = useMemo(() => {
    return chapterGroups.flatMap((g) => g.entries)
  }, [chapterGroups])

  const refetchChapterSectionsFromServer = useCallback(async () => {
    if (!bookId) {
      return
    }
    try {
      const res = await apiService.getChapterSectionsToc(bookId)
      if (res.success && Array.isArray(res.data)) {
        setChapterSectionsFromServer(res.data)
      }
    } catch (e) {
      logger.warn('refetchChapterSectionsFromServer failed', e)
    }
  }, [bookId])

  const runChapterSectionsRefreshAfterSave = useCallback(() => {
    if (!bookId) {
      return
    }
    void Promise.all([apiService.getChapterSectionsToc(bookId), apiService.getBook(bookId)]).then(([res, bookRes]) => {
      if (res.success && Array.isArray(res.data)) {
        setChapterSectionsFromServer(res.data)
        const ch = chaptersRef.current

        // Use the fresh book from the server (picks up any server-side
        // reconciliation that migrated chapterSectionKeys in MongoDB).
        const freshBook = bookRes.success && bookRes.data ? bookRes.data : bookRef.current
        if (!freshBook) {
          return
        }
        if (bookRes.success && bookRes.data) {
          setBook(bookRes.data)
        }

        const canonicalKeys = canonicalChapterSectionKeysFromServer(ch, res.data)
        const prevChapterDefs = getBookChapters(freshBook)

        const { chapters: nextChapterDefs } = prevChapterDefs?.length
          ? mergeChaptersWithCanonicalKeys(prevChapterDefs, canonicalKeys)
          : { chapters: [{ id: generateChapterId(), title: '', chapterSections: canonicalKeys }] }

        // New ^ sections do NOT auto-append to flatSectionsOrder; they land in
        // the TOC's Unplaced pool until the author places them via the Organize
        // dialog or by dragging in the Sections view.
        const prevJson = JSON.stringify(prevChapterDefs ?? null)
        const nextJson = JSON.stringify(nextChapterDefs)
        const chaptersChanged = prevJson !== nextJson

        if (!chaptersChanged) {
          return
        }

        const nextSettings = {
          ...freshBook.settings,
          chapters: nextChapterDefs,
        }

        void apiService
          .updateBook(bookId, { settings: nextSettings })
          .then((up) => {
            if (up.success && up.data) {
              setBook(up.data)
            }
          })
          .catch((e) => {
            logger.warn('Could not sync chapters after save', e)
          })
      }
    })
  }, [bookId, bookRef, chaptersRef, setBook])

  // ── Persist helper: optimistic update + API call ──

  const persistChapterDefs = useCallback(
    async (nextDefs: Chapter[], errorMsg: string, nextDeps?: ChapterSectionDependency[]) => {
      if (!bookId || !book) {
        return
      }
      const previousBook = book
      const nextSettings = {
        ...book.settings,
        chapters: nextDefs,
        ...(nextDeps !== undefined ? { chapterSectionDependencies: nextDeps } : {}),
      }
      setBook({
        ...book,
        settings: nextSettings,
      })
      try {
        await apiService.updateBook(bookId, { settings: nextSettings })
        await refetchChapterSectionsFromServer()
      } catch (err) {
        logger.error(errorMsg, err)
        toast.error(errorMsg)
        setBook(previousBook)
      }
    },
    [bookId, book, setBook, refetchChapterSectionsFromServer],
  )

  // ── Section reorder (called from ChapterSectionsToc with pre-built defs) ──

  const handleSectionDragEnd = useCallback(
    async (nextDefs: Chapter[], nextDeps?: ChapterSectionDependency[]) => {
      // The organize dialogs rebuild chapters from their own drafts — id, title and
      // sections only — so saving one would drop every chapter note. Neither dialog
      // edits notes, so carry them across by id here, at the single point both arrive.
      const notesById = new Map((getBookChapters(book) ?? []).map((ch) => [ch.id, ch.note]))
      const withNotes = nextDefs.map((ch) => {
        const note = ch.note ?? notesById.get(ch.id)
        return note ? { ...ch, note } : ch
      })
      await persistChapterDefs(withNotes, 'Failed to reorder chapter sections', nextDeps)
    },
    [book, persistChapterDefs],
  )

  // ── Flat sections order (used when book.settings.structure === 'flat') ──

  const persistFlatSectionsOrder = useCallback(
    async (nextOrder: string[]) => {
      if (!bookId || !book) {
        return
      }
      const previousBook = book
      const nextSettings = { ...book.settings, flatSectionsOrder: nextOrder }
      setBook({ ...book, settings: nextSettings })
      try {
        await apiService.updateBook(bookId, { settings: nextSettings })
      } catch (err) {
        logger.error('Failed to save flat sections order', err)
        toast.error('Failed to save sections order')
        setBook(previousBook)
      }
    },
    [bookId, book, setBook],
  )

  // ── Per-element section order (author planning aid; book.settings.elementSectionOrder) ──

  const persistElementSectionOrder = useCallback(
    async (nextOrder: Record<string, string[]>) => {
      if (!bookId || !book) {
        return
      }
      const previousBook = book
      const nextSettings = { ...book.settings, elementSectionOrder: nextOrder }
      setBook({ ...book, settings: nextSettings })
      try {
        await apiService.updateBook(bookId, { settings: nextSettings })
      } catch (err) {
        logger.error('Failed to save element section order', err)
        toast.error('Failed to save element order')
        setBook(previousBook)
      }
    },
    [bookId, book, setBook],
  )

  // ── Section dependency CRUD (called from the editor-level AddDependencyDialog) ──

  const persistDependencies = useCallback(
    async (nextDeps: ChapterSectionDependency[], errorMsg: string) => {
      if (!bookId || !book) {
        return
      }
      const previousBook = book
      const nextSettings = { ...book.settings, chapterSectionDependencies: nextDeps }
      setBook({ ...book, settings: nextSettings })
      try {
        await apiService.updateBook(bookId, { settings: nextSettings })
      } catch (err) {
        logger.error(errorMsg, err)
        toast.error(errorMsg)
        setBook(previousBook)
      }
    },
    [bookId, book, setBook],
  )

  const upsertSectionDependency = useCallback(
    async (fromKey: string, toKey: string, reason: string, kind: DependencyKind) => {
      if (!book || fromKey === toKey) {
        return
      }
      const current = book.settings.chapterSectionDependencies ?? []
      const trimmedReason = reason.trim()
      const existing = findDependency(current, fromKey, toKey)
      const next = existing
        ? current.map((d) => (d.id === existing.id ? { ...d, reason: trimmedReason, kind } : d))
        : [
            ...current,
            {
              id: generateDependencyId(),
              fromKey,
              toKey,
              kind,
              reason: trimmedReason,
              createdAt: new Date().toISOString(),
            },
          ]
      await persistDependencies(next, 'Failed to save dependency')
    },
    [book, persistDependencies],
  )

  const removeSectionDependency = useCallback(
    async (depId: string) => {
      if (!book) {
        return
      }
      const current = book.settings.chapterSectionDependencies ?? []
      const next = current.filter((d) => d.id !== depId)
      await persistDependencies(next, 'Failed to delete dependency')
    },
    [book, persistDependencies],
  )

  // ── Chapter CRUD ──

  /** Inserts an empty chapter directly before `chapterId`, and returns its id so the caller can name it. */
  const addChapterBefore = useCallback(
    async (chapterId: string) => {
      const currentDefs = getBookChapters(book) ?? []
      const idx = currentDefs.findIndex((ch) => ch.id === chapterId)
      if (idx === -1) {
        return undefined
      }
      const newChapter: Chapter = { id: generateChapterId(), title: '', chapterSections: [] }
      const nextDefs = [...currentDefs.slice(0, idx), newChapter, ...currentDefs.slice(idx)]
      await persistChapterDefs(nextDefs, 'Failed to create chapter')
      return newChapter.id
    },
    [book, persistChapterDefs],
  )

  const renameChapter = useCallback(
    async (chapterId: string, newTitle: string) => {
      const currentDefs = getBookChapters(book) ?? []
      const nextDefs = currentDefs.map((ch) => (ch.id === chapterId ? { ...ch, title: newTitle } : ch))
      await persistChapterDefs(nextDefs, 'Failed to rename chapter')
    },
    [book, persistChapterDefs],
  )

  /** Saves the chapter's working note; empty text drops the field rather than storing ''. */
  const setChapterNote = useCallback(
    async (chapterId: string, note: string) => {
      const currentDefs = getBookChapters(book) ?? []
      const trimmed = note.trim()
      const nextDefs = currentDefs.map((ch) => {
        if (ch.id !== chapterId) {
          return ch
        }
        if (!trimmed) {
          const { note: _cleared, ...withoutNote } = ch
          return withoutNote
        }
        return { ...ch, note: trimmed }
      })
      await persistChapterDefs(nextDefs, 'Failed to save chapter note')
    },
    [book, persistChapterDefs],
  )

  const deleteChapter = useCallback(
    async (chapterId: string) => {
      const currentDefs = getBookChapters(book) ?? []
      const toDelete = currentDefs.find((ch) => ch.id === chapterId)
      if (!toDelete) {
        return
      }
      // Remove chapter — its sections become unattached
      const nextDefs = currentDefs.filter((ch) => ch.id !== chapterId)
      await persistChapterDefs(nextDefs, 'Failed to delete chapter')
    },
    [book, persistChapterDefs],
  )

  const reorderChapters = useCallback(
    async (activeId: string, overId: string) => {
      const currentDefs = getBookChapters(book) ?? []
      const oldIndex = currentDefs.findIndex((ch) => ch.id === activeId)
      const newIndex = currentDefs.findIndex((ch) => ch.id === overId)
      if (oldIndex === -1 || newIndex === -1) {
        return
      }
      const nextDefs = arrayMove(currentDefs, oldIndex, newIndex)
      await persistChapterDefs(nextDefs, 'Failed to reorder chapters')
    },
    [book, persistChapterDefs],
  )

  // Seed chapters on first load when book has no chapters yet
  useEffect(() => {
    if (!bookId || !book || !isAuthor) {
      return
    }
    if (getBookChapters(book) !== undefined) {
      return
    }
    if (chaptersInitRef.current) {
      return
    }
    if (chapterSectionsFromServer === null) {
      return
    }
    const keys = canonicalChapterSectionKeysFromServer(chapters, chapterSectionsFromServer)
    if (keys.length === 0) {
      return
    }
    chaptersInitRef.current = true
    const chapterDefs: Chapter[] = [{ id: generateChapterId(), title: '', chapterSections: keys }]

    let cancelled = false
    ;(async () => {
      try {
        const res = await apiService.updateBook(bookId, {
          settings: { ...book.settings, chapters: chapterDefs },
        })
        if (!cancelled && res.success && res.data) {
          setBook(res.data)
        }
      } catch (e) {
        chaptersInitRef.current = false
        logger.error('Failed to initialize chapters', e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [bookId, book, isAuthor, chapterSectionsFromServer, chapters, setBook])

  return {
    chapterSectionsFromServer,
    chapterSectionEntries,
    chapterGroups,
    entryByKey,
    handleSectionDragEnd,
    persistFlatSectionsOrder,
    persistElementSectionOrder,
    runChapterSectionsRefreshAfterSave,
    refetchChapterSectionsFromServer,
    addChapterBefore,
    renameChapter,
    setChapterNote,
    deleteChapter,
    reorderChapters,
    upsertSectionDependency,
    removeSectionDependency,
  }
}
